import { FC, useState, useCallback, useMemo } from "react"
import { LoanOfficer } from "../../../../types"
import {
  LOMap,
  LOMapEntry,
  OBLOResult,
  PricingEngineType
} from "../../../../types/pricing-engine"
import {
  useLazyFindOBLOsQuery,
  useClearOBCacheMutation
} from "../../../../redux/queries/pricing-engine"
import { pushToast } from "../../../../redux/slices/notifications"
import { useAppDispatch } from "../../../hooks"
import { PrimaryBtn, LightBtn } from "../../buttons"
import LOMapTable from "./lo-map-table"
import OBLOAutocomplete from "./ob-lo-autocomplete"
import Select from "../../form/select"
import { RefreshCw, UserPlus } from "lucide-react"

interface Props {
  rateflowId: string
  brandId: string
  currentLoMap: LOMap
  members: LoanOfficer[]
  onChange: (newLoMap: LOMap) => void
  engineType?: PricingEngineType
}

const LOMapManager: FC<Props> = ({
  rateflowId,
  brandId,
  currentLoMap,
  members,
  onChange,
  engineType
}) => {
  const dispatch = useAppDispatch()
  const [selectedOBLO, setSelectedOBLO] = useState<OBLOResult | null>(null)
  const [selectedMemberId, setSelectedMemberId] = useState<string>("")

  const [findOBLOs, { isFetching: isSyncingAll }] = useLazyFindOBLOsQuery()
  const [clearOBCache, { isLoading: isClearingCache }] =
    useClearOBCacheMutation()

  // Helper to get member's primary ID (prefer loid, fallback to uid)
  const getMemberId = (m: LoanOfficer): string => m.loid || m.uid || ""

  // Helper to check if a member matches an ID (could be loid, uid, or nid)
  const memberMatchesId = (m: LoanOfficer, id: string): boolean =>
    String(m.loid) === String(id) ||
    String(m.uid) === String(id) ||
    String(m.nid) === String(id)

  // Filter members that are not already in the lo_map
  const availableMembers = useMemo(() => {
    const mappedIds = Object.keys(currentLoMap)

    return members.filter(m => {
      const memberId = getMemberId(m)
      if (!memberId) return false

      // Check if this member is already mapped (by any of their IDs)
      const isMapped = mappedIds.some(id => memberMatchesId(m, id))

      return !isMapped
    })
  }, [members, currentLoMap])

  const memberOptions = useMemo(
    () =>
      availableMembers.map(m => ({
        id: getMemberId(m),
        name: `${m.first_name} ${m.last_name} (${m.email})`
      })),
    [availableMembers]
  )

  const handleSelectOBLO = useCallback((obLO: OBLOResult) => {
    setSelectedOBLO(obLO)
    setSelectedMemberId("")
  }, [])

  const handleClearOBLO = useCallback(() => {
    setSelectedOBLO(null)
    setSelectedMemberId("")
  }, [])

  const handleAddMapping = useCallback(() => {
    if (!selectedOBLO || !selectedMemberId) {
      dispatch(
        pushToast({
          msg: "Please select both an OB LO and a member",
          theme: "warning",
          timeout: 3000
        })
      )
      return
    }

    const entry: LOMapEntry = {
      biz_channel: String(selectedOBLO.biz_channel.id),
      originator_id: String(selectedOBLO.index)
    }

    const newLoMap = { ...currentLoMap, [selectedMemberId]: entry }
    onChange(newLoMap)

    // Reset selections
    setSelectedOBLO(null)
    setSelectedMemberId("")

    dispatch(
      pushToast({
        msg: "LO mapping added",
        theme: "success",
        timeout: 2000
      })
    )
  }, [selectedOBLO, selectedMemberId, currentLoMap, onChange, dispatch])

  const handleSyncAll = useCallback(async () => {
    try {
      const result = await findOBLOs({ brand_id: brandId }).unwrap()
      if (!result || result.length === 0) {
        dispatch(
          pushToast({
            msg: "No Optimal Blue LOs found for this brand",
            theme: "warning",
            timeout: 3000
          })
        )
        return
      }

      const mappedIds = Object.keys(currentLoMap)
      const newLoMap = { ...currentLoMap }
      let addedCount = 0

      result.forEach(obLO => {
        // Try to match by email
        const matchedMember = members.find(m => m.email === obLO.email)
        if (matchedMember) {
          const memberId = getMemberId(matchedMember)
          if (!memberId) return

          // Check if already mapped (by any of their IDs)
          const isMapped = mappedIds.some(id =>
            memberMatchesId(matchedMember, id)
          )

          if (!isMapped) {
            const entry: LOMapEntry = {
              biz_channel: String(obLO.biz_channel.id),
              originator_id: String(obLO.index)
            }
            newLoMap[memberId] = entry
            addedCount++
          }
        }
      })

      if (addedCount === 0) {
        dispatch(
          pushToast({
            msg: "No new LO matches found",
            theme: "info",
            timeout: 3000
          })
        )
        return
      }

      onChange(newLoMap)
      dispatch(
        pushToast({
          msg: `Added ${addedCount} LO mapping(s)`,
          theme: "success",
          timeout: 3000
        })
      )
    } catch (error) {
      dispatch(
        pushToast({
          msg: "Failed to sync LOs",
          theme: "danger",
          timeout: 3000
        })
      )
    }
  }, [findOBLOs, brandId, members, currentLoMap, onChange, dispatch])

  const handleClearCache = useCallback(async () => {
    try {
      await clearOBCache().unwrap()
      dispatch(
        pushToast({
          msg: "Cache cleared successfully",
          theme: "success",
          timeout: 3000
        })
      )
    } catch (error) {
      dispatch(
        pushToast({
          msg: "Failed to clear cache",
          theme: "danger",
          timeout: 3000
        })
      )
    }
  }, [clearOBCache, dispatch])

  const handleDeleteMapping = useCallback(
    (loid: string) => {
      const newLoMap = { ...currentLoMap }
      delete newLoMap[loid]
      onChange(newLoMap)
    },
    [currentLoMap, onChange]
  )

  return (
    <div className="space-y-6">
      {/* Add New LO Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-4">Add New LO Mapping</h4>

        <div className="flex gap-4 mb-4">
          <LightBtn
            onClick={handleClearCache}
            disabled={isClearingCache}
            className="flex items-center"
          >
            <RefreshCw
              size={16}
              className={`mr-2 ${isClearingCache ? "animate-spin" : ""}`}
            />
            Clear OB Cache
          </LightBtn>

          <PrimaryBtn
            onClick={handleSyncAll}
            disabled={isSyncingAll}
            className="flex items-center"
          >
            <RefreshCw
              size={16}
              className={`mr-2 ${isSyncingAll ? "animate-spin" : ""}`}
            />
            Auto-Sync by Email
          </PrimaryBtn>
        </div>

        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <OBLOAutocomplete
              brandId={brandId}
              onSelect={handleSelectOBLO}
              onClear={handleClearOBLO}
              selectedOBLO={selectedOBLO}
            />
          </div>

          {selectedOBLO && memberOptions.length > 0 && (
            <div className="w-72">
              <Select
                label="Select Member"
                name="member"
                data={memberOptions}
                selected={selectedMemberId}
                setSelected={(value: string) => setSelectedMemberId(value)}
              />
            </div>
          )}

          {selectedOBLO && memberOptions.length === 0 && (
            <div className="text-amber-600 text-sm py-2">
              All members are already mapped
            </div>
          )}

          {selectedOBLO && selectedMemberId && (
            <PrimaryBtn
              onClick={handleAddMapping}
              className="flex items-center"
            >
              <UserPlus size={16} className="mr-2" />
              Add Mapping
            </PrimaryBtn>
          )}
        </div>
      </div>

      {/* Current Mappings Table */}
      <div>
        <h4 className="font-medium mb-4">
          Current LO Mappings ({Object.keys(currentLoMap).length})
        </h4>

        <LOMapTable
          loMap={currentLoMap}
          members={members}
          onDelete={handleDeleteMapping}
          onUpdateEntry={(loid, entry) => {
            const newLoMap = { ...currentLoMap, [loid]: entry }
            onChange(newLoMap)
          }}
        />
      </div>
    </div>
  )
}

export default LOMapManager
